/**
 * กรงเล็บคีมไฟ (tongs-claw): fire + soot, motif sparks (re-themed from blood to ember 10-13). All eight moves
 * hand-made in the sample-page style (the seven plain moves below bc-maul share its pieces, each its own shape, motion and timing); bc-maul, the lock routine พยัคฆ์กดเหยื่อ, is
 * timed to the rules' beats (packages/shared data/longMoves/bloodClaw.ts; the page times are the beats + MAUL_T0, so
 * fx/longBloodClaw.ts can pin them to the live beats). Pieces: the pounce dust, three parallel hot-iron claw streaks per
 * rake, sparks (flying, then scorch spots on the ground: the burn), a ring of dust pixels kicked up by the roar (no glow),
 * the crossed claw sets of the upward tear, the fall's dust. Original designs.
 */
import { add, crescent, diamond, hash, isoRing, lerp, line, px, type G, type V } from '../kit';
import type { Scene } from '../skills';
import { drawRuns, runsOf } from '../moonDiscCache';
import { C, clamp01, fade, hitStar, level, puffs, ringOn, since, strikeAt, tilesPx, type ArtFx } from '../families/swordKit';

// ── Tones ──

const WH = C('white', 8);
// Hot iron, hottest to coolest (10-13: blood -> ember): orange, rust, cooling cinnabar iron, soot.
const H2 = C('brown', 2);
const H3 = C('fire', 3);
const H4 = C('fire', 2);
const H5 = C('fire', 3);
const H6 = C('fire', 4);
const F3 = C('fire', 3);
const F4 = C('fire', 4);
const D1 = C('brown', 1);
const D4 = C('brown', 4);
const D5 = C('brown', 5);
const D6 = C('brown', 6);
const DW = C('white', 4);

// ── Timing: the data's beats + MAUL_T0 (the 0.45 s flurry wind-up before them) ──

const MAUL_T0 = 0.5;
const B_POUNCE = MAUL_T0;
const B_MOUNT = MAUL_T0 + 0.45;
const B_RAKE = [MAUL_T0 + 0.95, MAUL_T0 + 1.4] as const;
const B_FLURRY = MAUL_T0 + 1.85;
const B_FLIP = MAUL_T0 + 2.75;
const B_ROAR = MAUL_T0 + 3.3;
const B_REND = MAUL_T0 + 3.9;
const B_FALL = MAUL_T0 + 4.5;
const B_STANCE = MAUL_T0 + 4.75;
const B_END = MAUL_T0 + 5.2;
/** The blows (the rules spread a beat's blows over the first 0.6 of it). */
const spread = (at: number, to: number, n: number): number[] => Array.from({ length: n }, (_, j) => at + ((j + 0.5) / n) * 0.6 * (to - at));
const H_RAKE = [spread(B_RAKE[0], B_RAKE[1], 1)[0]!, spread(B_RAKE[1], B_FLURRY, 1)[0]!] as const;
const H_FLURRY = spread(B_FLURRY, B_FLIP, 4);
const H_REND = spread(B_REND, B_FALL, 1)[0]!;

// ── The art's pieces ──

/** Where the downed foe's body lies (the rakes land low: a foe on its back). */
const preyAt = (s: Scene, k = 0.38): V => strikeAt(s, k);

/**
 * One streak from `a` to `b`, `w` rows at its widest (1 -> w -> 1, peak at 0.6) on the lower side: the `edge` rows in
 * one pass, then the 1-cell `core`, so the pen sees two style sets per streak, not one per cell.
 */
function streak(g: G, a: V, b: V, w: number, core: string, edge: string): void {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const n = Math.max(1, Math.round(Math.max(Math.abs(dx), Math.abs(dy))));
  const l = Math.hypot(dx, dy) || 1;
  // The lower side's step (screen y down): the perpendicular with a positive y, else +x.
  let ox = -dy / l;
  let oy = dx / l;
  if (oy < 0 || (oy === 0 && ox < 0)) {
    ox = -ox;
    oy = -oy;
  }
  const sx = Math.abs(ox) > Math.abs(oy) ? Math.sign(ox) : 0;
  const sy = sx ? 0 : 1;
  if (w > 1) {
    g.fillStyle = edge;
    for (let i = 0; i <= n; i++) {
      const u = i / n;
      const prof = u < 0.6 ? u / 0.6 : (1 - u) / 0.4;
      const rows = Math.round(1 + (w - 1) * Math.min(1, prof * 1.4));
      if (rows < 2) continue;
      const x = Math.round(a[0] + dx * u);
      const y = Math.round(a[1] + dy * u);
      if (sy) g.fillRect(x, y + 1, 1, rows - 1);
      else g.fillRect(sx > 0 ? x + 1 : x - rows + 1, y, rows - 1, 1);
    }
  }
  line(g, a, b, core);
}

/**
 * Three parallel claw streaks from `c` along `dir` (unit), `L` long, `gap` apart: drawn on over two frames (age `e`),
 * then the body dims and the dark scar stays. Thick hot body, a white leading cell row on the fresh frames.
 */
function clawSet(g: G, c: V, dir: V, L: number, gap: number, e: number, life: number, w = 3): void {
  if (e < 0 || e >= life) return;
  const nx = -dir[1];
  const ny = dir[0];
  const on = clamp01((e + 1) / 2);
  const lv = fade(e, Math.ceil(life * 0.45), Math.ceil(life * 0.75));
  level(g, lv);
  for (let k = -1; k <= 1; k++) {
    const o = k * gap;
    const len = L * (k === 0 ? 1 : 0.82) * on;
    const a: V = [c[0] + nx * o - (dir[0] * L) / 2 + dir[0] * (k === 0 ? 0 : L * 0.09), c[1] + ny * o - (dir[1] * L) / 2 + dir[1] * (k === 0 ? 0 : L * 0.09)];
    const b: V = [a[0] + dir[0] * len, a[1] + dir[1] * len];
    if (e < 2) streak(g, a, b, w, WH, H5);
    else if (e < life * 0.45) streak(g, a, b, w, H6, H4);
    else streak(g, a, b, Math.max(2, w - 1), H3, H2);
  }
  g.globalAlpha = 1;
}

/** Sparks out of `c`: thrown on arcs (dir x, up), 2x2 drops then single cells; `n` of them over `life` frames. */
function flecks(g: G, c: V, e: number, n: number, seed: number, dirX: number, speed: number, life: number): void {
  if (e < 0 || e >= life) return;
  for (let i = 0; i < n; i++) {
    const h = hash(i, seed);
    const h2 = hash(i, seed + 3);
    const vx = (dirX * (0.4 + h * 0.9) + (h2 - 0.5) * 0.9) * speed;
    const vy = -(0.9 + h2 * 1.3) * speed;
    const x = c[0] + vx * e;
    const y = c[1] + vy * e + 0.55 * e * e;
    level(g, e < life * 0.5 ? 3 : 2);
    g.fillStyle = i % 3 ? H4 : H5;
    const big = e < life * 0.5 && i % 2 === 0;
    g.fillRect(Math.round(x), Math.round(y), big ? 2 : 1, big ? 2 : 1);
    if (big) px(g, x, y, H6);
  }
  g.globalAlpha = 1;
}

/** The spots the flecks leave on the ground under the prey (the burn): `n` small iso spots, dimming over `life`. */
function spots(g: G, c: V, e: number, n: number, seed: number, w: number, life: number): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.5, life * 0.8));
  for (let i = 0; i < n; i++) {
    const x = Math.round(c[0] + (hash(i, seed) - 0.5) * w);
    const y = Math.round(c[1] + (hash(i, seed + 1) - 0.5) * w * 0.35);
    g.fillStyle = H2;
    g.fillRect(x - 1, y, i % 2 ? 3 : 2, 1);
    px(g, x, y, H3);
  }
  g.globalAlpha = 1;
}

/** A spark falling off the prey (the burn ticking): one 1x2 spark every few frames. */
function drips(g: G, c: V, t: number, from: number, to: number, seed: number): void {
  if (t < from || t >= to) return;
  const e = since(t, from);
  for (let i = 0; i < 3; i++) {
    const ph = (e + i * 5) % 9;
    const x = c[0] + (hash(i, seed) - 0.5) * 10;
    level(g, ph < 6 ? 3 : 2);
    g.fillStyle = H4;
    g.fillRect(Math.round(x), Math.round(c[1] + ph * 1.2), 1, 2);
  }
  g.globalAlpha = 1;
}

/**
 * The roar's ring: dust pixels in a broken iso ring (every cell picked by a hash, clumps of 1-2), cached as runs per
 * (radius, tone, density: the wide area rings of the plain moves are sparser, `dens` samples per radius cell); expanding,
 * the inner rings dimmer.
 */
function dustRing(g: G, c: V, rx: number, lvl: number, light: boolean, dens = 6.5): void {
  if (rx < 2 || lvl <= 0) return;
  const r = Math.round(rx);
  const runs = runsOf(`bcRing|${r}|${light ? 1 : 0}|${dens}`, (put) => {
    const n = Math.max(16, Math.round(r * dens));
    for (let i = 0; i < n; i++) {
      const h = hash(i, r);
      if (h < 0.3) continue;
      const a = (i / n) * Math.PI * 2;
      const jr = r + (hash(i, r + 9) - 0.5) * 3;
      const x = Math.round(Math.cos(a) * jr);
      const y = Math.round(Math.sin(a) * jr * 0.5);
      const front = Math.sin(a) > 0;
      const css = light ? (h > 0.7 ? DW : D6) : h > 0.75 ? D6 : D5;
      put(x, y, css, front ? 3 : 2);
      if (h > 0.55) put(x + 1, y, front ? D5 : D4, front ? 3 : 2);
      if (h > 0.8) put(x, y - 1, DW, front ? 3 : 1);
    }
  });
  drawRuns(g, runs, c[0], c[1], lvl);
}

/** The wind-up's heat: a red dither rising off the crouched back, hooked-finger glints. */
function heat(g: G, s: Scene, from: number, to: number): void {
  if (s.t < from || s.t >= to) return;
  const b = s.body(s.t);
  const e = since(s.t, from);
  const back: V = [b[0] - s.d[0] * 4, b[1] - s.chest * 0.85];
  for (let i = 0; i < 14; i++) {
    const ph = (e + Math.floor(hash(i, 41) * 6)) % 6;
    const x = back[0] + (hash(i, 42) - 0.5) * 16;
    const y = back[1] - ph * 2 - hash(i, 43) * 4;
    level(g, ph < 2 ? 2 : 1);
    px(g, x, y, ph < 3 ? F3 : H4);
  }
  g.globalAlpha = 1;
  const { hand, handB } = s.limbs(s.t);
  if (e % 3 !== 2)
    for (const h of [hand, handB]) {
      level(g, 3);
      diamond(g, h[0], h[1], 1, H5, e % 2 ? F4 : WH);
    }
  g.globalAlpha = 1;
}

// ── bc-maul (the lock routine, 5.2 s): pounce, mount, rakes L/R, the flurry, the roll, the roar, the upward tear ──

const FLURRY_DIR: readonly V[] = [
  [0.78, 0.62],
  [-0.7, 0.71],
  [0.92, 0.38],
  [-0.86, 0.5],
];

const maul: ArtFx = {
  id: 'tongs-claw-bc-maul',
  skill: 'bc-maul',
  th: 'bc-maul',
  on: true,
  note: 'ท่ายาว (ล็อก 5.2 วิ): ย่อตัวสี่ขา ไอแดงลอยจากหลัง -> ตะครุบ ฝุ่นพุ่งจากจุดกระโดดและจุดล้ม เงาร่างแยก -> คร่อมเหยื่อ -> ข่วนซ้าย ข่วนขวา (สามรอยเล็บเหล็กร้อนขนาน สะเก็ดไฟกระเซ็น) -> ข่วนรัวสี่ครั้ง -> พลิกตัวเหยื่อ สะเก็ดไฟสาด เงาร่างแยก -> คำราม วงฝุ่นแตกกระจายบนพื้น -> ฉีกขึ้นสองเล็บไขว้ เป้ากระเด็น -> ฝุ่นตอนล้ม',
  weapon: 'none',
  acts: [
    { m: 'wxLockPalm', t0: 0.02, t1: B_POUNCE },
    { m: 'wxSlam', t0: B_POUNCE, t1: B_MOUNT },
    { m: 'wxStomp', t0: B_MOUNT, t1: B_RAKE[0] },
    { m: 'wxCleave', t0: B_RAKE[0], t1: B_RAKE[1] },
    { m: 'wxCleave', t0: B_RAKE[1], t1: B_FLURRY },
    { m: 'wxBreak', t0: B_FLURRY, t1: B_FLIP },
    { m: 'wxQi', t0: B_FLIP, t1: B_ROAR },
    { m: 'wxPower', t0: B_ROAR, t1: B_REND },
    { m: 'wxRise', t0: B_REND, t1: B_FALL },
    { m: 'wxBuff', t0: B_FALL, t1: B_END },
  ],
  hits: [...H_RAKE, ...H_FLURRY, H_REND],
  big: H_REND,
  ghosts: [B_POUNCE + 0.05, B_POUNCE + 0.13, B_POUNCE + 0.22, B_FLIP + 0.08, B_FLIP + 0.2],
  stepIn: 12,
  cast: [0.02, MAUL_T0],
  pre: MAUL_T0,
  hit: H_RAKE[0],
  end: B_END,
  afterimage: 'port',
  beats: [
    { t: 0.02, th: 'ย่อสี่ขา' },
    { t: B_POUNCE, th: 'ตะครุบ' },
    { t: B_MOUNT, th: 'คร่อม' },
    { t: B_RAKE[0], th: 'ข่วนซ้าย' },
    { t: B_RAKE[1], th: 'ข่วนขวา' },
    { t: B_FLURRY, th: 'ข่วนรัว x4' },
    { t: B_FLIP, th: 'พลิก' },
    { t: B_ROAR, th: 'คำราม' },
    { t: B_REND, th: 'ฉีกขึ้น' },
    { t: B_FALL, th: 'ล้ม' },
  ],
  under(g, s) {
    // pounceDust: the launch kick behind the caster, the landing burst on the prey.
    puffs(g, [s.a[0] - s.d[0] * 4, s.a[1]], since(s.t, B_POUNCE), 12, 4, 2.8, 'brown', 8, 0.4, 11);
    puffs(g, [s.f[0], s.f[1] + 1], since(s.t, B_MOUNT - 0.1), 18, 6, 3.4, 'brown', 10, 0.5, 13);
    // The burn on the ground: scorch spots under the prey from the first rake to the fall.
    const ground: V = [s.f[0] - s.d[0] * 4, s.f[1] + 2];
    spots(g, ground, since(s.t, H_RAKE[0]), 4, 21, 14, 50);
    spots(g, ground, since(s.t, H_FLURRY[1]!), 6, 23, 22, 40);
    spots(g, [ground[0] + s.d[0] * 4, ground[1]], since(s.t, B_FLIP + 0.1), 8, 25, 30, 30);
    // roarRing: three broken rings of dust pixels off the caster's feet.
    for (let j = 0; j < 3; j++) {
      const e = since(s.t, B_ROAR + 0.08 + j * 0.12);
      if (e < 0 || e >= 8) continue;
      const b = s.body(s.t);
      dustRing(g, [b[0], b[1] + 1], 10 + e * 7 + j * 3, fade(e, 3, 6), j === 0);
    }
    // The fall: dust where the prey lands.
    puffs(g, [s.f[0], s.f[1] + 1], since(s.t, B_FALL), 16, 5, 3, 'brown', 9, 0.4, 17);
  },
  over(g, s) {
    heat(g, s, 0.02, B_POUNCE + 0.1);
    const prey = preyAt(s);
    // The rakes: left then right, a big set each, flecks off the far side.
    for (let j = 0; j < 2; j++) {
      const e = since(s.t, H_RAKE[j]!);
      const dir: V = j ? [-0.62, 0.78] : [0.62, 0.78];
      clawSet(g, [prey[0], prey[1] - 4], dir, 32, 7, e, 10, 4);
      flecks(g, [prey[0] + dir[0] * 13, prey[1] + 6], e, 9, 31 + j, j ? -1 : 1, 2, 8);
      hitStar(g, s, prey, e, 'fire', 0.8);
    }
    // The flurry: four smaller sets, alternating, stacked a little up and down the body.
    for (let j = 0; j < 4; j++) {
      const e = since(s.t, H_FLURRY[j]!);
      const c: V = [prey[0] + (j % 2 ? 4 : -4), prey[1] - 5 + ((j * 3) % 5) * 2 - 4];
      clawSet(g, c, FLURRY_DIR[j]!, 24, 6, e, 6, 3);
      flecks(g, c, e, 4, 35 + j, j % 2 ? -1 : 1, 1.4, 5);
      if (j === 3) hitStar(g, s, c, e, 'fire', 0.9);
    }
    // The burn ticking on the prey between the rakes and the tear.
    drips(g, [prey[0], prey[1] + 1], s.t, H_RAKE[0] + 0.25, B_REND, 51);
    // sparkFleck: the roll throws a spray up and over.
    flecks(g, [prey[0], prey[1] - 2], since(s.t, B_FLIP + 0.1), 16, 61, s.d[0] >= 0 ? 1 : -1, 2.3, 10);
    // The roar: dust kicked up in a tight burst round the caster's head.
    {
      const e = since(s.t, B_ROAR + 0.05);
      if (e >= 0 && e < 6) {
        const b = s.body(s.t);
        const head: V = [b[0] + s.d[0] * 3, b[1] - s.chest * 1.55];
        level(g, fade(e, 2, 4));
        for (let i = 0; i < 10; i++) {
          const a = -Math.PI * (0.1 + 0.8 * (i / 9));
          const r0 = 7 + e * 3.5;
          const p: V = [head[0] + Math.cos(a) * r0, head[1] + Math.sin(a) * r0 * 0.7];
          const q: V = [head[0] + Math.cos(a) * (r0 + 3), head[1] + Math.sin(a) * (r0 + 3) * 0.7];
          line(g, p, q, i % 2 ? D6 : DW);
        }
        g.globalAlpha = 1;
      }
    }
    // clawCross: two upward claw sets crossing on the chest, then the foe thrown with a long spray.
    {
      const e = since(s.t, H_REND);
      const c = strikeAt(s, 0.9);
      const lead = since(s.t, B_REND + 0.04);
      if (lead >= 0 && e < 0) {
        // The rising sweep before the tear: two short red streaks climbing from the ground.
        level(g, 2);
        const k = clamp01((s.t - B_REND) / Math.max(0.01, H_REND - B_REND));
        for (const sx of [-1, 1]) line(g, [c[0] + sx * 12, c[1] + 24], [c[0] + sx * 7, c[1] + 24 - 20 * k], H5);
        g.globalAlpha = 1;
      }
      clawSet(g, [c[0] - 2, c[1]], [0.55, -0.83], 44, 8, e, 11, 5);
      clawSet(g, [c[0] + 2, c[1]], [-0.55, -0.83], 44, 8, e - 1, 10, 5);
      flecks(g, c, e - 1, 18, 71, s.d[0] >= 0 ? 1 : -1, 2.8, 10);
      hitStar(g, s, c, e, 'fire', 1.6);
    }
  },
};

// ── Shared pieces of the seven plain moves ──

const sideOf = (s: Scene): number => (s.d[0] >= 0 ? 1 : -1);
/** A dash's length: `D`, but stopping a body short of the foe (the claws land on it, the body doesn't stand in it). */
const reach = (s: Scene, D: number): number => Math.min(D, Math.max(0, Math.hypot(s.f[0] - s.a[0], s.f[1] - s.a[1]) - 34));

/** Sparks circling over the foe's head (dazed and burning): three 2-cell sparks on a flat orbit. */
function sparkDaze(g: G, s: Scene, e: number, life: number): void {
  if (e < 0 || e >= life) return;
  const head: V = [s.f[0], s.f[1] - s.chest * 1.85];
  level(g, fade(e, life - 5, life - 2));
  for (let i = 0; i < 3; i++) {
    const a = e * 0.6 + (i * Math.PI * 2) / 3;
    const x = Math.round(head[0] + Math.cos(a) * 8);
    const y = Math.round(head[1] + Math.sin(a) * 2.5);
    const front = Math.sin(a) >= 0;
    g.fillStyle = front ? H5 : H3;
    g.fillRect(x, y - 1, front ? 2 : 1, 2);
    px(g, x, y - 2, front ? H6 : H4);
  }
  g.globalAlpha = 1;
}

/** Heat drawn out of the foe's burn back to the caster's chest along an arc (the drain): `n` drops staggered. */
function drain(g: G, from: V, to: V, e: number, n: number, flight: number, h: number): void {
  if (e < 0) return;
  for (let i = 0; i < n; i++) {
    const u = (e - i * 1.2) / flight;
    if (u <= 0 || u >= 1) continue;
    const p = lerp(from, to, u);
    const y = p[1] - Math.sin(u * Math.PI) * h;
    const q = lerp(from, to, Math.max(0, u - 0.08));
    const qy = q[1] - Math.sin(Math.max(0, u - 0.08) * Math.PI) * h;
    level(g, 2);
    line(g, [q[0], qy], [p[0], y], H4);
    level(g, 3);
    g.fillStyle = i % 2 ? H5 : H4;
    g.fillRect(Math.round(p[0]), Math.round(y), 2, 2);
    px(g, p[0], y, H6);
  }
  g.globalAlpha = 1;
}

function gouges(g: G, c: V, dir: V, L: number, e: number, life: number): void {
  if (e < 0 || e >= life || L < 4) return;
  const d: V = [dir[0], dir[1] * 0.5];
  const n: V = [-dir[1], dir[0] * 0.5];
  level(g, fade(e, life * 0.5, life * 0.8));
  for (let k = -1; k <= 1; k++) {
    // Three parallel tapered cuts: dark core (2 cells wide mid-length, 1 at the ends), a dirt lip thrown up on the far side.
    const Lk = L * (k ? 0.82 : 1);
    const o = add(c, n, k * 6.5);
    for (let i = 0; i < Lk; i++) {
      const t = i / Lk;
      const h = hash(i, 170 + k);
      if (h < 0.14 && i > 1 && i < Lk - 2) continue;
      const hw = Math.sin(t * Math.PI) > 0.5 ? 2 : 1;
      const p = add(o, d, i - Lk / 2);
      px(g, p[0], p[1], hw > 1 ? D1 : H2);
      if (hw > 1) px(g, p[0], p[1] + 1, e < 3 ? H3 : D1);
      if (h > 0.3) px(g, p[0], p[1] - 1, h > 0.75 ? D6 : D5);
      if (h > 0.9) px(g, p[0] + 1, p[1] - 2, D4);
    }
  }
  g.globalAlpha = 1;
}

/** The claws flexing: a glint on each hand (white then fire), from `from` to `to`. */
function clawGlint(g: G, s: Scene, from: number, to: number, r = 1): void {
  if (s.t < from || s.t >= to) return;
  const e = since(s.t, from);
  if (e % 3 === 2) return;
  const { hand, handB } = s.limbs(s.t);
  level(g, 3);
  for (const h of [hand, handB]) diamond(g, h[0], h[1], r, H5, e % 2 ? F4 : WH);
  g.globalAlpha = 1;
}

/** Embers of boiling heat rising round the body: 2x2 cinders climbing, popping into a 4-cell cross at the top. */
function boil(g: G, s: Scene, from: number, to: number, n: number, spread: number): void {
  if (s.t < from || s.t >= to) return;
  const e = since(s.t, from);
  const b = s.body(s.t);
  for (let i = 0; i < n; i++) {
    const ph = (e + Math.floor(hash(i, 91) * 9)) % 9;
    const x = Math.round(b[0] + (hash(i, 92) - 0.5) * spread);
    const y = Math.round(b[1] - 2 - s.chest * (0.2 + hash(i, 93) * 0.9) - ph * 2);
    if (ph < 7) {
      level(g, ph < 4 ? 3 : 2);
      g.fillStyle = ph < 4 ? H4 : H5;
      g.fillRect(x, y, 2, 2);
      px(g, x, y, ph < 4 ? H6 : F4);
    } else {
      level(g, 2);
      for (const [dx, dy] of [[-1, 0], [2, 0], [0, -1], [1, 2]] as const) px(g, x + dx, y + dy, H5);
    }
  }
  g.globalAlpha = 1;
}

/** A dash's claw trails: three short hot lines dragged behind the body at hand height. */
function clawTrail(g: G, s: Scene, e: number, life: number): void {
  if (e < 0 || e >= life) return;
  const b = s.body(s.t);
  level(g, fade(e, life - 3, life - 1));
  for (let k = 0; k < 3; k++) {
    const y = b[1] - s.chest * (0.55 + k * 0.18);
    const x0 = b[0] - s.d[0] * 4;
    const L = 16 + k * 4 - e;
    line(g, [x0, y], [x0 - s.d[0] * L, y - s.d[1] * L], k === 1 ? H6 : H4);
    px(g, x0, y, WH);
  }
  g.globalAlpha = 1;
}

// ── 1. bc-rake: claws flexed, a step in, one big diagonal rake, sparks off the far side, the burn starts ──

const RAKE_HIT = 0.42;
const rake: ArtFx = {
  id: 'tongs-claw-bc-rake',
  skill: 'bc-rake',
  th: 'bc-rake',
  on: true,
  note: 'งอนิ้วเป็นกรงเล็บ ประกายปลายเล็บ ไอแดงจากหลัง -> ก้าวเข้าตะปบเฉียงลง สามรอยเล็บแดงใหญ่ขนาน เงาร่างแยก -> สะเก็ดไฟกระเซ็นไปฝั่งไกล ตกลงพื้นเป็นรอยไหม้ (ไฟลวก) -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxCleave', t0: 0.12, t1: 0.8 }],
  hits: [RAKE_HIT],
  big: RAKE_HIT,
  ghosts: [0.32, 0.38],
  stepIn: 8,
  pre: 0.3,
  hit: RAKE_HIT,
  end: 1.6,
  afterimage: 'port',
  under(g, s) {
    spots(g, [s.f[0] + sideOf(s) * 4, s.f[1] + 2], since(s.t, RAKE_HIT + 0.1), 6, 101, 18, 14);
  },
  over(g, s) {
    heat(g, s, 0.08, RAKE_HIT - 0.05);
    const e = since(s.t, RAKE_HIT);
    if (e < 0) return;
    const sd = sideOf(s);
    const c = strikeAt(s, 0.95);
    const dir: V = [sd * 0.58, 0.81];
    clawSet(g, c, dir, 40, 8, e, 11, 4);
    flecks(g, [c[0] + dir[0] * 14, c[1] + dir[1] * 10], e, 10, 103, sd, 2.1, 9);
    drips(g, [s.f[0], s.f[1] - s.chest * 0.6], s.t, RAKE_HIT + 0.2, RAKE_HIT + 1.1, 105);
    hitStar(g, s, c, e, 'fire', 1);
  },
};

// ── 2. bc-crush (break): a fire ring tell, both claws high, a leap down, two sets driven through the guard, ground cracked ──

const CRUSH_HIT = 0.8;
const crush: ArtFx = {
  id: 'tongs-claw-bc-crush',
  skill: 'bc-crush',
  th: 'bc-crush',
  on: true,
  note: 'วงไฟส้มวาดรอบเท้า ยกสองกรงเล็บสูง ประกายเล็บ (ตั้งท่าทำลาย) -> กระโจนบดลง สองชุดรอยเล็บแนวดิ่งคู่กันทะลุการ์ด เงาร่างแยก -> เศษการ์ดกระเด็น พื้นใต้เป้าแตกร้าว ฝุ่น -> ประกายไฟวนเหนือหัว (มึน + ไฟลวก)',
  weapon: 'none',
  acts: [{ m: 'wxBreak', t0: 0.15, t1: 1.2 }],
  hits: [CRUSH_HIT],
  big: CRUSH_HIT,
  ghosts: [0.62, 0.68, 0.74],
  stepIn: 11,
  cast: [0.05, 0.5],
  pre: 0.5,
  hit: CRUSH_HIT,
  end: 2.4,
  afterimage: 'port',
  under(g, s) {
    if (s.t >= 0.05 && s.t < CRUSH_HIT) {
      level(g, 3);
      ringOn(g, s.a, 15, -Math.PI / 2, (s.t - 0.05) / 0.45, F4, true);
      g.globalAlpha = 1;
    }
    const e = since(s.t, CRUSH_HIT);
    if (e < 0 || e >= 18) return;
    // Cracks out from under the foe: jagged dark lines, a lit lip on the first frames.
    level(g, fade(e, 9, 14));
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + hash(i, 111) * 0.5;
      const L = Math.min(1, (e + 1) / 3) * (14 + hash(i, 112) * 10);
      const mid: V = [s.f[0] + Math.cos(a + 0.25) * L * 0.5, s.f[1] + 1 + Math.sin(a + 0.25) * L * 0.25];
      const end: V = [s.f[0] + Math.cos(a) * L, s.f[1] + 1 + Math.sin(a) * L * 0.5];
      line(g, [s.f[0], s.f[1] + 1], mid, D4);
      line(g, mid, end, D4);
      if (e < 3) px(g, mid[0], mid[1] - 1, D6);
    }
    g.globalAlpha = 1;
    puffs(g, [s.f[0], s.f[1] + 1], e, 14, 6, 3, 'brown', 9, 0.4, 113);
    spots(g, [s.f[0], s.f[1] + 2], e - 2, 6, 115, 20, 16);
  },
  over(g, s) {
    clawGlint(g, s, 0.15, CRUSH_HIT - 0.05, 2);
    heat(g, s, 0.1, 0.5);
    const e = since(s.t, CRUSH_HIT);
    if (e < 0) return;
    const c = strikeAt(s, 0.95);
    // Two sets driven straight down, side by side, the second a frame behind.
    clawSet(g, [c[0] - 6, c[1]], [-0.14, 0.99], 42, 6, e, 11, 4);
    clawSet(g, [c[0] + 6, c[1]], [0.14, 0.99], 42, 6, e - 1, 10, 4);
    // The guard breaking: bone-pale shards flung out and falling.
    if (e >= 1 && e < 10) {
      level(g, fade(e, 5, 8));
      for (let i = 0; i < 8; i++) {
        const a = -Math.PI * (0.05 + 0.9 * (i / 7));
        const v = 2.4 + hash(i, 117) * 1.6;
        const x = c[0] + Math.cos(a) * v * e;
        const y = c[1] - 4 + Math.sin(a) * v * e * 0.8 + 0.35 * e * e;
        if (i % 2) diamond(g, x, y, 1, D6, WH);
        else line(g, [x, y], [x + Math.cos(a) * 3, y + 1], DW);
      }
      g.globalAlpha = 1;
    }
    flecks(g, c, e - 1, 10, 119, sideOf(s), 1.8, 9);
    hitStar(g, s, c, e, 'fire', 1.3);
    sparkDaze(g, s, e - 4, 18);
  },
};

// ── 3. bc-pounce: crouched heat, a long leaping dash (split-body ghosts), a slam on landing: dust ring, gouges, a claw set ──

const PN_GO = 0.45;
const PN_LAND = 0.9;
const PN_D = Math.min(110, Math.round(tilesPx(6) * 0.6));
const PN_R = tilesPx(1.8);
const pounce: ArtFx = {
  id: 'tongs-claw-bc-pounce',
  skill: 'bc-pounce',
  th: 'bc-pounce',
  on: true,
  note: 'ย่อตัวต่ำ ไอแดงจากหลัง ฝุ่นใต้เท้า -> กระโจนโค้งไกล รอยเล็บสามเส้นลากตามมือ เงาร่างแยกตลอดทาง -> ตกลงตะครุบ รอยเล็บแดงตบลงเป้า วงฝุ่นแตกเท่ารัศมีกระแทก รอยเล็บสามเส้นบนพื้น รอยไหม้ -> จาง',
  weapon: 'none',
  acts: [
    { m: 'wxThrust', t0: 0.12, t1: PN_LAND },
    { m: 'wxSlam', t0: PN_LAND - 0.05, t1: 1.6 },
  ],
  hits: [PN_LAND],
  big: PN_LAND,
  ghosts: [0.55, 0.64, 0.73, 0.82],
  pre: PN_GO,
  hit: PN_LAND,
  end: 2.4,
  dash: PN_D,
  afterimage: 'dash',
  move(s, t) {
    const k0 = clamp01((t - PN_GO) / (PN_LAND - PN_GO));
    const k = 1 - (1 - k0) * (1 - k0);
    const back = 1 - clamp01((t - 2.0) / 0.3);
    const lift = Math.sin(k0 * Math.PI) * 16;
    const D = reach(s, PN_D);
    return [s.d[0] * D * k * back, (s.d[1] * D * k - lift) * back];
  },
  under(g, s) {
    puffs(g, [s.a[0] - s.d[0] * 3, s.a[1]], since(s.t, PN_GO), 10, 4, 2.6, 'brown', 8, 0.3, 121);
    const e = since(s.t, PN_LAND);
    if (e < 0) return;
    const land = add(s.a, s.d, reach(s, PN_D));
    puffs(g, [land[0], land[1] + 1], e, 14, 6, 3.4, 'brown', 10, 0.5, 123);
    if (e < 8) dustRing(g, [land[0], land[1] + 1], PN_R * (0.35 + 0.65 * clamp01(e / 4)), fade(e, 4, 6), true, 4);
    gouges(g, add(land, s.d, 6), s.d, 22, e, 24);
    spots(g, [s.f[0], s.f[1] + 2], e - 1, 7, 125, 22, 22);
  },
  over(g, s) {
    heat(g, s, 0.05, PN_GO + 0.05);
    clawTrail(g, s, since(s.t, PN_GO), 6);
    const e = since(s.t, PN_LAND);
    if (e < 0) return;
    const sd = sideOf(s);
    const c = strikeAt(s, 0.7);
    clawSet(g, c, [sd * 0.3, 0.95], 42, 8, e, 11, 5);
    flecks(g, c, e, 12, 127, sd, 2.2, 9);
    hitStar(g, s, c, e, 'fire', 1.3);
  },
};

// ── 4. bc-roar: heat boiling off the body, the chest drawn in, a roar: rays and sound arcs, dust rings to the edge, dazed ──

const ROAR_HIT = 0.7;
const ROAR_R = Math.min(72, tilesPx(3));
const roar: ArtFx = {
  id: 'tongs-claw-bc-roar',
  skill: 'bc-roar',
  th: 'bc-roar',
  on: true,
  note: 'วงแดงวาดบอกรัศมีรอบตัว ถ่านไฟเดือดผุดลอยรอบกาย ฝุ่นถูกดูดเข้าอก -> คำราม เส้นเสียงพุ่งจากหัว คลื่นเสียงโค้งสามชั้น วงฝุ่นแตกสี่ระลอกออกถึงขอบ -> ศัตรูรอบตัวมึน ประกายไฟวนเหนือหัว ถ่านไฟยังเดือด -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxPower', t0: 0.05, t1: 1.5 }],
  hits: [ROAR_HIT],
  big: ROAR_HIT,
  cast: [0.05, 0.5],
  pre: 0.45,
  hit: ROAR_HIT,
  end: 2.3,
  afterimage: null,
  under(g, s) {
    if (s.t >= 0.1 && s.t < ROAR_HIT) {
      level(g, 2);
      ringOn(g, s.a, ROAR_R, -Math.PI / 2, (s.t - 0.1) / 0.5, H4, true);
      g.globalAlpha = 1;
    }
    const e = since(s.t, ROAR_HIT);
    if (e < 0 || e > 14) return;
    for (let j = 0; j < 3; j++) {
      const ee = e - j * 3;
      if (ee < 0 || ee >= 7) continue;
      dustRing(g, [s.a[0], s.a[1] + 1], 12 + (ROAR_R - 12) * clamp01((ee + 1) / 6) - j * 3, fade(ee, 3, 5), j === 0, 3.5);
    }
    if (e < 5) {
      level(g, fade(e, 2, 4));
      isoRing(g, s.a[0], s.a[1], Math.round(ROAR_R * clamp01((e + 1) / 4)), H5, e < 2);
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    boil(g, s, 0.1, ROAR_HIT + 1.1, 12, 26);
    const b = s.body(s.t);
    const head: V = [b[0] + s.d[0] * 3, b[1] - s.chest * 1.55];
    // The in-breath: dust drawn in to the chest.
    if (s.t >= 0.2 && s.t < ROAR_HIT) {
      const k = clamp01((s.t - 0.2) / (ROAR_HIT - 0.25));
      level(g, 2);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + 0.3;
        const r = 30 * (1 - k) + 6;
        const p: V = [head[0] + Math.cos(a) * r, head[1] + 8 + Math.sin(a) * r * 0.6];
        const q: V = [head[0] + Math.cos(a) * (r + 3), head[1] + 8 + Math.sin(a) * (r + 3) * 0.6];
        line(g, p, q, i % 2 ? D6 : DW);
      }
      g.globalAlpha = 1;
    }
    const e = since(s.t, ROAR_HIT);
    if (e < 0) return;
    // The roar: rays out of the head all round the upper half, then sound arcs bowing out in front and behind.
    if (e < 7) {
      level(g, fade(e, 3, 5));
      for (let i = 0; i < 14; i++) {
        const a = -Math.PI * (0.02 + 0.96 * (i / 13));
        const r0 = 8 + e * 5;
        const L = i % 2 ? 4 : 6;
        line(g, [head[0] + Math.cos(a) * r0, head[1] + Math.sin(a) * r0 * 0.75], [head[0] + Math.cos(a) * (r0 + L), head[1] + Math.sin(a) * (r0 + L) * 0.75], i % 3 ? H5 : WH);
      }
      g.globalAlpha = 1;
    }
    for (let k = 0; k < 3; k++) {
      const ee = e - k * 2;
      if (ee < 0 || ee >= 6) continue;
      level(g, fade(ee, 2, 4));
      const r = 12 + ee * 6;
      for (const sg of [-1, 1]) {
        const a = sg > 0 ? 0 : Math.PI;
        crescent(g, head[0], head[1] + 2, r, a - 0.75, a + 0.75, k === 0 ? 3 : 2, k === 0 ? WH : H6, H4, 0.85, false);
      }
    }
    g.globalAlpha = 1;
    sparkDaze(g, s, e - 1, 10);
  },
};

// ── 5. bc-feast: a hooked claw into the wound, held; heat drawn back in a stream to the caster's chest (lifesteal) ──

const FEAST_HIT = 0.55;
const feast: ArtFx = {
  id: 'tongs-claw-bc-feast',
  skill: 'bc-feast',
  th: 'bc-feast',
  on: true,
  note: 'ประกายเล็บ -> ก้าวเข้าขย้ำแผล รอยเล็บโค้งงอขึ้นค้างอยู่บนเป้า เงาร่างแยก -> สายประกายไฟโค้งไหลจากรอยไหม้กลับเข้าอก (ดูดพลัง) อกกะพริบแดง รอยไหม้ใต้เป้า -> จาง',
  weapon: 'none',
  acts: [
    { m: 'wxThrust', t0: 0.15, t1: 0.85 },
    { m: 'wxQi', t0: 0.85, t1: 1.8 },
  ],
  hits: [FEAST_HIT],
  big: FEAST_HIT,
  ghosts: [0.44, 0.5],
  stepIn: 9,
  pre: 0.35,
  hit: FEAST_HIT,
  end: 2.2,
  afterimage: 'port',
  under(g, s) {
    spots(g, [s.f[0], s.f[1] + 2], since(s.t, FEAST_HIT), 8, 131, 24, 22);
  },
  over(g, s) {
    clawGlint(g, s, 0.1, FEAST_HIT);
    const e = since(s.t, FEAST_HIT);
    if (e < 0) return;
    const sd = sideOf(s);
    const c = strikeAt(s, 0.9);
    // The hook: a set raked upward and held, pulsing while the heat is drawn.
    if (e < 14) {
      const hold = e < 3 ? e : 3 + ((e - 3) % 4 === 0 ? 0 : 1);
      clawSet(g, c, [-sd * 0.42, -0.91], 34, 7, hold, 16, 4);
    }
    flecks(g, c, e, 8, 133, -sd, 1.6, 7);
    hitStar(g, s, c, e, 'fire', 1);
    // The drain: a stream of drops arcing back to the caster's chest.
    const chest: V = [s.a[0] + s.d[0] * 2, s.a[1] - s.chest * 0.95];
    const de = since(s.t, FEAST_HIT + 0.15);
    if (de < 14) drain(g, c, chest, de, 8, 5, 16);
    if (de >= 5 && de < 16 && de % 2 === 1) {
      level(g, de < 12 ? 3 : 2);
      diamond(g, chest[0], chest[1], 2, H5, de < 10 ? WH : H6);
      g.globalAlpha = 1;
    }
  },
};

// ── 6. bc-fury (counter stance): a dust ring settles round the feet, three claw marks held up as a ward; when struck, they burst ──

const FURY_TRIP = 1.45;
const fury: ArtFx = {
  id: 'tongs-claw-bc-fury',
  skill: 'bc-fury',
  th: 'bc-fury',
  on: true,
  note: 'ตั้งท่ารอ วงฝุ่นวงแดงรอบเท้า สามรอยเล็บแดงตั้งเป็นโล่จางๆ ด้านหน้า กะพริบช้า -> (เมื่อรับท่าได้) รอยเล็บสว่างขาวแตกเป็นสะเก็ดไฟกระเซ็น ไอร้อนพุ่งขึ้นรอบตัว ถ่านไฟเดือด (เตาพยัคฆ์เดือด) -> จาง',
  weapon: 'none',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.3 }],
  hits: [],
  pre: 0.1,
  hit: 0.6,
  end: 2.8,
  afterimage: null,
  under(g, s) {
    const e = since(s.t, 0.1);
    if (e < 0 || e > 30) return;
    if (s.t < FURY_TRIP) {
      level(g, 2);
      ringOn(g, s.a, 16, Math.PI / 2, (s.t - 0.1) / 0.4, H4, true);
      g.globalAlpha = 1;
      dustRing(g, [s.a[0], s.a[1] + 1], Math.min(14, 6 + e * 2), e % 6 < 3 ? 2 : 1, false);
    } else {
      const t = since(s.t, FURY_TRIP);
      if (t < 8) dustRing(g, [s.a[0], s.a[1] + 1], 14 + t * 5, fade(t, 3, 6), true, 4.5);
    }
  },
  over(g, s) {
    const sd = sideOf(s);
    const b = s.body(s.t);
    const o: V = [b[0] + s.d[0] * 32, b[1] - s.chest * 0.95];
    // The ward: three upright claw marks in front, drawn on, then held dim and breathing.
    const w = since(s.t, 0.35);
    if (w >= 0 && s.t < FURY_TRIP) {
      const on = clamp01((w + 1) / 4);
      level(g, w < 4 ? 3 : w % 6 < 4 ? 2 : 1);
      for (let k = -1; k <= 1; k++) {
        const x = o[0] + k * 5 * sd;
        const top = o[1] - 13 + Math.abs(k) * 2;
        streak(g, [x - sd, top], [x + sd, top + 26 * on - Math.abs(k) * 4], 3, w < 2 ? WH : H5, H3);
      }
      g.globalAlpha = 1;
    }
    clawGlint(g, s, 0.15, FURY_TRIP);
    // The trip: the marks flash and burst, heat surging up, embers boiling.
    const e = since(s.t, FURY_TRIP);
    if (e < 0) return;
    if (e < 2) {
      level(g, 3);
      for (let k = -1; k <= 1; k++) {
        const x = o[0] + k * 5 * sd;
        const top = o[1] - 13 + Math.abs(k) * 2;
        streak(g, [x - sd, top], [x + sd, top + 26 - Math.abs(k) * 4], 4, WH, H6);
      }
      g.globalAlpha = 1;
    }
    flecks(g, o, e - 1, 14, 141, sd, 2.2, 9);
    heat(g, s, FURY_TRIP, FURY_TRIP + 0.9);
    boil(g, s, FURY_TRIP, FURY_TRIP + 1.2, 10, 22);
  },
};

// ── 7. bc-tiger (power): a fire ring, a long dash gouging the ground, rake, rake, then giant jaws closing (3 blows, drain) ──

const TG_GO = 0.55;
const TG_ARR = 0.8;
const TG_H = [0.88, 1.12, 1.45] as const;
const TG_D = Math.min(110, Math.round(tilesPx(5) * 0.6));
const tiger: ArtFx = {
  id: 'tongs-claw-bc-tiger',
  skill: 'bc-tiger',
  th: 'bc-tiger',
  on: true,
  note: 'ท่าพลัง: วงไฟวาดรอบเท้า ไอแดงจากหลัง ถ่านไฟเดือด ประกายเล็บ -> พุ่งเข้าหาเป้า รอยเล็บขูดพื้นตามทาง เงาร่างแยก -> ข่วนซ้าย ข่วนขวา -> ขากรรไกรพยัคฆ์: รอยเล็บใหญ่บนล่างงับเข้าหากัน วงฝุ่นแตกสามระลอก สะเก็ดไฟสาด -> ประกายไฟไหลกลับเข้าอก (ดูดพลัง) เป้ามึน',
  weapon: 'none',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: TG_GO },
    { m: 'wxThrust', t0: TG_GO, t1: TG_H[0] },
    { m: 'wxCleave', t0: TG_H[0] - 0.05, t1: TG_H[1] - 0.04 },
    { m: 'wxCleave', t0: TG_H[1] - 0.04, t1: TG_H[2] - 0.12 },
    { m: 'wxRise', t0: TG_H[2] - 0.12, t1: 2.1 },
  ],
  hits: [...TG_H],
  big: TG_H[2],
  ghosts: [0.6, 0.66, 0.72, 0.78, TG_H[2] - 0.06],
  cast: [0.05, TG_GO],
  pre: TG_GO,
  hit: TG_H[0],
  end: 2.9,
  dash: TG_D,
  afterimage: 'dash',
  beats: [
    { t: 0.05, th: 'รวมแรง' },
    { t: TG_GO, th: 'พุ่ง' },
    { t: TG_H[0], th: 'ข่วนซ้าย' },
    { t: TG_H[1], th: 'ข่วนขวา' },
    { t: TG_H[2], th: 'ขย้ำ' },
  ],
  move(s, t) {
    const k0 = clamp01((t - TG_GO) / (TG_ARR - TG_GO));
    const k = 1 - (1 - k0) * (1 - k0);
    const back = 1 - clamp01((t - 2.5) / 0.3);
    const D = reach(s, TG_D);
    return [s.d[0] * D * k * back, s.d[1] * D * k * back];
  },
  under(g, s) {
    if (s.t >= 0.05 && s.t < TG_GO + 0.05) {
      level(g, 3);
      ringOn(g, s.a, 17, -Math.PI / 2, (s.t - 0.05) / 0.4, F4, true);
      g.globalAlpha = 1;
    }
    // Gouges along the dash path, laid as the body passes.
    const g0 = since(s.t, TG_GO);
    if (g0 >= 0) {
      const run = reach(s, TG_D) * clamp01(g0 / 4.4) - 10;
      gouges(g, add(s.a, s.d, 8 + run / 2), s.d, run, g0, 26);
    }
    const e = since(s.t, TG_H[2]);
    if (e < 0) return;
    for (let j = 0; j < 3; j++) {
      const ee = e - j * 2;
      if (ee >= 0 && ee < 8) dustRing(g, [s.f[0], s.f[1] + 1], 12 + ee * 7 + j * 3, fade(ee, 3, 6), j === 0, 4.5);
    }
    puffs(g, [s.f[0], s.f[1] + 1], e, 16, 6, 3, 'brown', 9, 0.4, 151);
    spots(g, [s.f[0], s.f[1] + 2], e, 10, 153, 30, 26);
  },
  over(g, s) {
    heat(g, s, 0.05, TG_GO);
    boil(g, s, 0.1, TG_GO, 8, 22);
    clawTrail(g, s, since(s.t, TG_GO), 5);
    const sd = sideOf(s);
    const c = strikeAt(s, 0.95);
    // Rake, rake.
    for (let j = 0; j < 2; j++) {
      const e = since(s.t, TG_H[j]!);
      const dir: V = [(j ? -sd : sd) * 0.6, 0.8];
      clawSet(g, [c[0], c[1] - 2], dir, 38, 7, e, 9, 4);
      flecks(g, add(c, dir, 12), e, 7, 155 + j, j ? -sd : sd, 1.8, 7);
      hitStar(g, s, c, e, 'fire', 0.9);
    }
    // The jaws: a huge set from above and one from below, closing on the foe.
    const e = since(s.t, TG_H[2]);
    if (e < 0) {
      // The tell for the jaws: both claws drawn back, glinting.
      clawGlint(g, s, TG_H[1] + 0.12, TG_H[2], 2);
      return;
    }
    const gap = Math.max(0, 8 - e * 4);
    clawSet(g, [c[0], c[1] - 10 - gap], [sd * 0.12, 0.99], 46, 9, e, 12, 5);
    clawSet(g, [c[0] + sd * 2, c[1] + 10 + gap], [sd * 0.12, -0.99], 46, 9, e, 12, 5);
    flecks(g, c, e, 20, 159, sd, 2.8, 11);
    hitStar(g, s, c, e, 'fire', 1.8);
    const chest: V = [s.body(s.t)[0], s.body(s.t)[1] - s.chest * 0.95];
    drain(g, c, chest, since(s.t, TG_H[2] + 0.25), 4, 4, 10);
    sparkDaze(g, s, e - 3, 12);
  },
};

/** The tongs-claw moves, hand-made: the seven plain moves and the bc-maul lock routine. */
export const BLOODCLAW_FX: readonly ArtFx[] = [rake, crush, maul, pounce, roar, feast, fury, tiger];

/** bc-maul's page times (s), for fx/longBloodClaw.ts to pin them to the live beats. */
export const BLOODCLAW_MAUL_T = {
  start: MAUL_T0,
  mount: B_MOUNT,
  rake: B_RAKE,
  flurry: B_FLURRY,
  flip: B_FLIP,
  roar: B_ROAR,
  rend: B_REND,
  fall: B_FALL,
  stance: B_STANCE,
  end: B_END,
} as const;
